// src/pages/Admin/Adherents/AdherentPdfGenerator.js

import jsPDF from "jspdf";

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

const COLORS = {
  primary: [1, 62, 142], // #013E8E
  green: [76, 168, 47], // #4CA82F
  slate900: [15, 23, 42],
  slate700: [51, 65, 85],
  slate500: [100, 116, 139],
  slate300: [203, 213, 225],
  slate100: [241, 245, 249],
  white: [255, 255, 255],
  amber: [245, 158, 11],
  rose: [225, 29, 72],
  emerald: [16, 185, 129],
};

function safe(value, fallback = "Non renseigné") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function formatDate(value) {
  if (!value) return "Non renseigné";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Non renseigné";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function yesNo(value) {
  return value ? "Oui" : "Non";
}

function getFullName(adherent) {
  return `${safe(adherent?.nom, "").toUpperCase()} ${safe(
    adherent?.prenom,
    ""
  )}`.trim();
}

function getGroupsText(adherent) {
  if (!Array.isArray(adherent?.Groups) || adherent.Groups.length === 0) {
    return "Non renseigné";
  }

  return adherent.Groups.map((group) => group?.nom).filter(Boolean).join(", ");
}

function getUsersText(adherent) {
  if (!Array.isArray(adherent?.Users) || adherent.Users.length === 0) {
    return "Non renseigné";
  }

  return adherent.Users.map((user) =>
    `${user?.nom || ""} ${user?.prenom || ""}`.trim()
  )
    .filter(Boolean)
    .join(", ");
}

function getAddress(adherent) {
  const address = [
    adherent?.adresse,
    [adherent?.cp, adherent?.commune].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  return safe(address);
}

function getPhotoUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

function getImageFormat(dataUrl) {
  if (String(dataUrl).startsWith("data:image/png")) return "PNG";
  if (String(dataUrl).startsWith("data:image/webp")) return "WEBP";
  return "JPEG";
}

async function loadImageAsDataUrl(url) {
  const source = getPhotoUrl(url);

  if (!source) return null;

  if (source.startsWith("data:image/")) return source;

  try {
    const response = await fetch(source, {
      mode: "cors",
      cache: "no-cache",
    });

    if (!response.ok) return null;

    const blob = await response.blob();

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function setFill(doc, color) {
  doc.setFillColor(color[0], color[1], color[2]);
}

function setText(doc, color) {
  doc.setTextColor(color[0], color[1], color[2]);
}

function setDraw(doc, color) {
  doc.setDrawColor(color[0], color[1], color[2]);
}

function drawFooter(doc, pageNumber) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  setDraw(doc, COLORS.slate300);
  doc.setLineWidth(0.2);
  doc.line(16, pageHeight - 16, pageWidth - 16, pageHeight - 16);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setText(doc, COLORS.slate500);

  doc.text(
    `Document généré le ${new Date().toLocaleDateString("fr-FR")} - MonAppliClub`,
    16,
    pageHeight - 10
  );

  doc.text(`Page ${pageNumber}`, pageWidth - 16, pageHeight - 10, {
    align: "right",
  });
}

function ensureSpace(doc, y, neededHeight, pageNumberRef) {
  const pageHeight = doc.internal.pageSize.getHeight();

  if (y + neededHeight <= pageHeight - 26) return y;

  drawFooter(doc, pageNumberRef.current);
  doc.addPage();
  pageNumberRef.current += 1;

  return 18;
}

function drawStatusPill(doc, x, y, label, type = "default") {
  const colors = {
    success: COLORS.emerald,
    warning: COLORS.amber,
    danger: COLORS.rose,
    default: COLORS.primary,
    green: COLORS.green,
  };

  const fill = colors[type] || colors.default;

  setFill(doc, fill);
  doc.roundedRect(x, y, 38, 8, 4, 4, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  setText(doc, COLORS.white);
  doc.text(label, x + 19, y + 5.5, { align: "center" });
}

function getStatusType(status) {
  switch (String(status || "")) {
    case "Licencié":
      return "success";
    case "Inscrit":
      return "warning";
    case "Cours d'essai":
    case "Cours d’essai":
      return "default";
    case "Non Licencié - Arrêt":
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "danger";
    default:
      return "green";
  }
}

function drawFieldBox(doc, x, y, width, label, value) {
  const safeValue = safe(value);
  const lines = doc.splitTextToSize(safeValue, width - 8);
  const limitedLines = lines.slice(0, 4);
  const height = Math.max(20, 11 + limitedLines.length * 5);

  setFill(doc, COLORS.white);
  setDraw(doc, COLORS.slate300);
  doc.setLineWidth(0.15);
  doc.roundedRect(x, y, width, height, 3, 3, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  setText(doc, COLORS.slate500);
  doc.text(label.toUpperCase(), x + 4, y + 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setText(doc, COLORS.slate900);
  doc.text(limitedLines, x + 4, y + 12);

  return height;
}

function drawSectionTitle(doc, title, y) {
  setText(doc, COLORS.primary);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(title, 16, y);

  setDraw(doc, COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(16, y + 3, 194, y + 3);

  return y + 9;
}

function drawTwoColumnSection(doc, title, items, y, pageNumberRef) {
  y = ensureSpace(doc, y, 20, pageNumberRef);
  y = drawSectionTitle(doc, title, y);

  const leftX = 16;
  const rightX = 108;
  const boxWidth = 86;
  const gapY = 4;

  for (let i = 0; i < items.length; i += 2) {
    const left = items[i];
    const right = items[i + 1];

    y = ensureSpace(doc, y, 28, pageNumberRef);

    const leftHeight = drawFieldBox(
      doc,
      leftX,
      y,
      boxWidth,
      left.label,
      left.value
    );

    let rightHeight = 0;

    if (right) {
      rightHeight = drawFieldBox(
        doc,
        rightX,
        y,
        boxWidth,
        right.label,
        right.value
      );
    }

    y += Math.max(leftHeight, rightHeight) + gapY;
  }

  return y + 4;
}

function drawNotesSection(doc, notes, y, pageNumberRef) {
  y = ensureSpace(doc, y, 38, pageNumberRef);
  y = drawSectionTitle(doc, "Notes", y);

  const width = 178;
  const lines = doc.splitTextToSize(safe(notes, "RAS"), width - 8);
  const height = Math.max(28, 12 + lines.length * 5);

  y = ensureSpace(doc, y, height, pageNumberRef);

  setFill(doc, COLORS.white);
  setDraw(doc, COLORS.slate300);
  doc.setLineWidth(0.15);
  doc.roundedRect(16, y, width, height, 3, 3, "FD");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setText(doc, COLORS.slate900);
  doc.text(lines, 20, y + 9);

  return y + height + 8;
}

function drawSignatureSection(doc, y, pageNumberRef) {
  y = ensureSpace(doc, y, 52, pageNumberRef);

  const boxWidth = 84;
  const boxHeight = 34;

  setText(doc, COLORS.primary);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Signatures", 16, y);

  y += 6;

  setDraw(doc, COLORS.slate300);
  setFill(doc, COLORS.white);

  doc.roundedRect(16, y, boxWidth, boxHeight, 3, 3, "FD");
  doc.roundedRect(110, y, boxWidth, boxHeight, 3, 3, "FD");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setText(doc, COLORS.slate500);

  doc.text("Signature de l'adhérent", 58, y + boxHeight - 6, {
    align: "center",
  });

  doc.text("Signature du représentant légal", 152, y + boxHeight - 6, {
    align: "center",
  });

  return y + boxHeight + 8;
}

async function drawHeader(doc, adherent) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const fullName = getFullName(adherent);

  setFill(doc, COLORS.primary);
  doc.rect(0, 0, pageWidth, 16, "F");

  setFill(doc, COLORS.green);
  doc.rect(0, 16, pageWidth, 2.5, "F");

  setFill(doc, COLORS.slate100);
  doc.roundedRect(16, 26, 178, 42, 5, 5, "F");

  const photoSize = 32;
  const photoX = 22;
  const photoY = 31;

  setFill(doc, COLORS.white);
  setDraw(doc, COLORS.slate300);
  doc.roundedRect(photoX, photoY, photoSize, photoSize, 5, 5, "FD");

  const photoDataUrl = await loadImageAsDataUrl(adherent?.photo_url);

  if (photoDataUrl) {
    try {
      doc.addImage(
        photoDataUrl,
        getImageFormat(photoDataUrl),
        photoX + 1.5,
        photoY + 1.5,
        photoSize - 3,
        photoSize - 3,
        undefined,
        "FAST"
      );
    } catch {
      drawPhotoFallback(doc, photoX, photoY, photoSize);
    }
  } else {
    drawPhotoFallback(doc, photoX, photoY, photoSize);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  setText(doc, COLORS.white);
  doc.text("MONAPPLICLUB", 16, 10);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setText(doc, COLORS.white);
  doc.text("Fiche d'inscription adhérent", pageWidth - 16, 10, {
    align: "right",
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  setText(doc, COLORS.slate700);
  doc.text("Fiche d'inscription de", 62, 38);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  setText(doc, COLORS.slate900);

  const nameLines = doc.splitTextToSize(fullName || "Adhérent", 120);
  doc.text(nameLines.slice(0, 2), 62, 50);

  drawStatusPill(
    doc,
    62,
    59,
    safe(adherent?.statut_inscription, "Statut"),
    getStatusType(adherent?.statut_inscription)
  );

  drawStatusPill(
    doc,
    104,
    59,
    safe(adherent?.saison_active, "Saison"),
    "green"
  );
}

function drawPhotoFallback(doc, x, y, size) {
  setFill(doc, COLORS.slate100);
  doc.roundedRect(x + 1.5, y + 1.5, size - 3, size - 3, 4, 4, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  setText(doc, COLORS.slate500);
  doc.text("?", x + size / 2, y + size / 2 + 5, { align: "center" });
}

const generateAdherentPdf = async (adherent) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageNumberRef = { current: 1 };

  await drawHeader(doc, adherent);

  let y = 80;

  y = drawTwoColumnSection(
    doc,
    "Informations personnelles",
    [
      { label: "Genre", value: adherent?.genre },
      { label: "Date de naissance", value: formatDate(adherent?.date_naissance) },
      { label: "Lieu de naissance", value: adherent?.lieu_naissance },
      { label: "Numéro de licence", value: adherent?.licence_number },
      {
        label: "Poids",
        value: adherent?.poids ? `${adherent.poids} kg` : "Non renseigné",
      },
      {
        label: "Autorisation photo",
        value: yesNo(adherent?.autorisation_photo),
      },
    ],
    y,
    pageNumberRef
  );

  y = drawTwoColumnSection(
    doc,
    "Coordonnées",
    [
      { label: "Email principal", value: adherent?.email },
      { label: "Email secondaire", value: adherent?.email_secondaire },
      { label: "Téléphone principal", value: adherent?.tel },
      { label: "Téléphone secondaire", value: adherent?.tel_secondaire },
      { label: "Représentant légal", value: adherent?.representant_legal },
      { label: "Adresse", value: getAddress(adherent) },
    ],
    y,
    pageNumberRef
  );

  y = drawTwoColumnSection(
    doc,
    "Informations club",
    [
      { label: "Statut", value: adherent?.statut_inscription },
      { label: "Saison active", value: adherent?.saison_active },
      { label: "Pass'Sport", value: adherent?.passsport_number },
      { label: "Pass'Club32", value: adherent?.passclub32_number },
      { label: "Groupes", value: getGroupsText(adherent) },
      { label: "Utilisateurs associés", value: getUsersText(adherent) },
    ],
    y,
    pageNumberRef
  );

  y = drawNotesSection(doc, adherent?.notes_adherent, y, pageNumberRef);
  y = drawSignatureSection(doc, y, pageNumberRef);

  drawFooter(doc, pageNumberRef.current);

  const fileName = `Fiche_Adherent_${safe(adherent?.nom, "Adherent")}_${safe(
    adherent?.prenom,
    ""
  )}`
    .replace(/\s+/g, "_")
    .replace(/[^\wÀ-ÿ-]/g, "");

  doc.save(`${fileName}.pdf`);
};

export default generateAdherentPdf;